import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardMedia,
  IconButton,
  Stack,
  LinearProgress,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

export type UploadItem =
  | File
  | {
      preview: string;
      name?: string;
      isExisting?: boolean;
      uploadedKB?: number;
      sizeKB?: number;
      complete?: boolean;
    }
  | string;

interface ToastInterface {
  error: (msg: string) => void;
}

interface FileUploadProps {
  label?: string;
  files?: UploadItem[];
  acceptedFileTypes?: string;
  onChange?: (files: File[]) => void;
  onDelete?: (name: string) => void;
  hint?: string;
  toast: ToastInterface;
}

type FileWithPreview = File & { preview?: string };

type DisplayItem = {
  key: string;
  name: string;
  preview: string;
  isFile: boolean;
  isExisting: boolean;
  original: UploadItem;
  sizeKB?: number;
  uploadedKB?: number;
  complete?: boolean;
};

const humanKB = (kb?: number) =>
  kb === undefined || kb === 0 ? "— KB" : `${Math.round(kb)} KB`;

const FileUpload: React.FC<FileUploadProps> = ({
  label = "Upload files",
  files = [],
  acceptedFileTypes = "image/*,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  onChange,
  onDelete,
  hint,
  toast,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const createdPreviewsRef = useRef<Set<string>>(new Set());
  const [displayItems, setDisplayItems] = useState<DisplayItem[]>([]);

  const normalize = (items: UploadItem[]) => {
    return items.map((item, idx) => {
      if (item instanceof File) {
        const file = item as FileWithPreview;
        if (!file.preview && file.type.startsWith("image/")) {
          const url = URL.createObjectURL(file);
          file.preview = url;
          createdPreviewsRef.current.add(url);
        }
        return {
          key: `file-${file.name}-${idx}`,
          name: file.name,
          preview: file.preview || "",
          isFile: true,
          isExisting: false,
          original: file,
          sizeKB: file.size / 1024,
          uploadedKB: file.size / 1024,
          complete: true,
        };
      }

      if (typeof item === "object" && item !== null && "preview" in item) {
        const obj = item as {
          preview: string;
          name?: string;
          isExisting?: boolean;
          sizeKB?: number;
          uploadedKB?: number;
          complete?: boolean;
        };
        const previewUrl = String(obj.preview);
        const name =
          obj.name || previewUrl.split("/").pop() || `existing-${idx}`;
        return {
          key: `existing-${name}-${idx}`,
          name,
          preview: previewUrl,
          isFile: false,
          isExisting: !!obj.isExisting,
          original: obj,
          sizeKB: obj.sizeKB,
          uploadedKB: obj.uploadedKB,
          complete: obj.complete ?? true,
        };
      }

      if (typeof item === "string") {
        const name = item.split("/").pop() || `file-${idx}`;
        return {
          key: `url-${name}-${idx}`,
          name,
          preview: item,
          isFile: false,
          isExisting: true,
          original: item,
          sizeKB: 0,
          uploadedKB: 0,
          complete: true,
        };
      }

      const fallback =
        typeof item === "object" && item !== null ? item : undefined;
      return {
        key: `unknown-${idx}`,
        name:
          (fallback && "name" in fallback ? String((fallback as { name?: unknown }).name) : "") ||
          `file-${idx}`,
        preview:
          fallback && "preview" in fallback
            ? String((fallback as { preview?: unknown }).preview || "")
            : "",
        isFile: false,
        isExisting: false,
        original: item,
        sizeKB:
          fallback && "sizeKB" in fallback
            ? Number((fallback as { sizeKB?: unknown }).sizeKB)
            : undefined,
        uploadedKB:
          fallback && "uploadedKB" in fallback
            ? Number((fallback as { uploadedKB?: unknown }).uploadedKB)
            : undefined,
        complete:
          fallback && "complete" in fallback
            ? Boolean((fallback as { complete?: unknown }).complete)
            : true,
      };
    });
  };

  useEffect(() => {
    setDisplayItems(normalize(files));
  }, [files]);

  const processFiles = (fileList: FileList | File[]) => {
    const newFiles = Array.from(fileList);
    const existingNames = new Set(displayItems.map((d) => d.name));

    const filtered: File[] = [];

    newFiles.forEach((f) => {
      if (existingNames.has(f.name)) {
        toast.error(`File "${f.name}" has already been uploaded.`);
      } else {
        filtered.push(f);
        if (!(f as FileWithPreview).preview && f.type.startsWith("image/")) {
          const url = URL.createObjectURL(f);
          (f as FileWithPreview).preview = url;
          createdPreviewsRef.current.add(url);
        }
      }
    });

    if (filtered.length > 0) onChange?.(filtered);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    processFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  const openFileDialog = () => inputRef.current?.click();
  const handleDeleteClick = (name: string) => onDelete?.(name);

  useEffect(() => {
    const previews = createdPreviewsRef.current;
    return () => {
      previews.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Failed to revoke object URL:", error);
        }
      });
      previews.clear();
    };
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {label}
      </Typography>

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={acceptedFileTypes}
        onChange={handleFileSelect}
      />

      <Box
        onClick={openFileDialog}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        sx={{
          border: "2px dashed",
          borderRadius: 2,
          textAlign: "center",
          py: 3,
          mb: 2,
          cursor: "pointer",
        }}
      >
        <CloudUploadOutlinedIcon
          sx={{ fontSize: 36, color: "text.secondary" }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Choose a file
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Or
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Drag and drop it here
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          {hint || "Guide: JPEG or PNG, max 2MB."}
        </Typography>

        <Button variant="outlined" sx={{ mt: 1 }}>
          Upload
        </Button>
      </Box>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ gap: 2 }}>
        {displayItems.map((item) => (
          <Card
            key={item.key}
            sx={{
              width: 150,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <CardMedia
              component="img"
              image={item.preview || ""}
              alt={item.name}
              sx={{ width: "100%", height: 100, objectFit: "cover" }}
            />
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" noWrap sx={{ mb: 0.5 }} fontSize={14}>
                {item.name}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize={10}
                >
                  {humanKB(item.uploadedKB)} of {humanKB(item.sizeKB)}
                </Typography>
                {item.complete && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      sx={{ color: "success.main" }}
                    />
                    <Typography variant="caption" sx={{ fontSize: 8 }}>
                      Complete
                    </Typography>
                  </Stack>
                )}
              </Stack>
              {item.sizeKB &&
                item.uploadedKB !== undefined &&
                item.uploadedKB < item.sizeKB && (
                  <LinearProgress
                    variant="determinate"
                    value={(item.uploadedKB / item.sizeKB) * 100}
                    sx={{ mt: 0.5, height: 4, borderRadius: 1 }}
                  />
                )}
            </Box>
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                bgcolor: "white",
                color: "error.light", // white trash icon
                border: "none",
                "&:hover": { bgcolor: "red" },
                zIndex: 10,
              }}
              onClick={() => handleDeleteClick(item.name)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default FileUpload;
